import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, Section, StatCard } from "@/components/admin/admin-kit";
import { admSystemHealth } from "@/lib/admin-pro.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/health")({
  head: () => privatePageMetadata("/nc/live/adm/health", "System health | Control room", "Internal system health."),
  component: AdminHealth,
});

function AdminHealth() {
  const run = useServerFn(admSystemHealth);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["adm-health"],
    queryFn: () => run({ data: undefined }),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Loading system health…</p>;

  const q = data.queues;

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Listings awaiting review" value={q.pendingListings} tone={q.pendingListings ? "warn" : "ok"} />
        <StatCard label="Services awaiting review" value={q.pendingServices} tone={q.pendingServices ? "warn" : "ok"} />
        <StatCard label="KYC/KYB in review" value={q.pendingKyc} tone={q.pendingKyc ? "warn" : "ok"} />
        <StatCard label="Open disputes" value={q.openDisputes} tone={q.openDisputes ? "warn" : "ok"} />
        <StatCard
          label="Withdrawals requested"
          value={q.requestedWithdrawals}
          tone={q.requestedWithdrawals ? "warn" : "ok"}
        />
        <StatCard label="Unpaid orders" value={q.unpaidOrders} />
        <StatCard label="Active boosts" value={q.activeBoosts} tone="hot" />
        <StatCard
          label="Last admin action"
          value={data.lastAuditAt ? new Date(data.lastAuditAt).toLocaleString() : "—"}
        />
      </div>

      <Section
        title="Work queues"
        description="Jump straight to whatever needs a human."
        actions={
          <Button variant="outline" size="sm" disabled={isFetching} onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      >
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/nc/live/adm/listings">Moderate listings ({q.pendingListings})</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/nc/live/adm/services">Moderate services ({q.pendingServices})</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/nc/live/adm/marketplace">KYC & disputes ({q.pendingKyc + q.openDisputes})</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/nc/live/adm/finance">Payouts ({q.requestedWithdrawals})</Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/nc/live/adm/logs">Audit log</Link>
          </Button>
        </div>
      </Section>

      <Section title="Recent payment events" description="Provider webhooks and payment processing.">
        <DataTable
          rows={data.payments}
          rowKey={(r) => r.id}
          empty="No payment events recorded."
          columns={[
            {
              key: "created_at",
              header: "When",
              cell: (r) => new Date(r.created_at).toLocaleString(),
              sortValue: (r) => r.created_at,
            },
            { key: "provider", header: "Provider", cell: (r) => r.provider },
            { key: "event", header: "Event", cell: (r) => <code className="text-xs">{r.event_type}</code> },
            {
              key: "processed",
              header: "Processed",
              cell: (r) =>
                r.processed_at ? (
                  <Badge variant="outline">{new Date(r.processed_at).toLocaleTimeString()}</Badge>
                ) : (
                  <Badge variant="destructive">pending</Badge>
                ),
            },
          ]}
        />
      </Section>

      <Section title="Site settings snapshot" description="Live platform configuration values.">
        <DataTable
          rows={data.settings}
          rowKey={(r) => String(r.key)}
          empty="No settings stored."
          columns={[
            { key: "key", header: "Key", cell: (r) => <code className="text-xs">{String(r.key)}</code>, sortValue: (r) => String(r.key) },
            {
              key: "value",
              header: "Value",
              className: "max-w-[520px]",
              cell: (r) => (
                <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-2 text-[11px]">
                  {JSON.stringify(r.value, null, 1)}
                </pre>
              ),
            },
          ]}
        />
      </Section>
    </div>
  );
}
